/**
 * useSourceControl Hook
 * EVI-405: Chat Input with Source Control (Pop-over, Badges & State)
 *
 * Custom hook for managing source control state in the chat input.
 * Provides:
 * - Current source selection state
 * - Dynamic placeholder text
 * - Toggle functions
 */

import { sourceControlToggleAtom, sourcesSelectedAtom, toggleLocalGuidelinesAtom, togglePrimaryEvidenceAtom } from '@/atoms/sourceControlAtoms';;
import type {
  ChatInputState,
  SourcesSelected,
} from '@/components/chat/types/sourceControl';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSourceControlMessages } from './useSourceControl.messages';

interface UseSourceControlOptions {
  /** The current input state (default or follow up) */
  inputState?: ChatInputState;
}

interface UseSourceControlReturn {
  /** Current computed source selection */
  sourcesSelected: SourcesSelected;
  /** Toggle states for individual source types */
  toggleState: {
    localGuidelines: boolean;
    primaryEvidence: boolean;
  };
  /** Dynamic placeholder text based on source selection and input state */
  placeholder: string;
  /** Toggle local/regional guidelines on/off */
  toggleLocalGuidelines: (checked: boolean) => void;
  /** Toggle Primary evidence on/off */
  togglePrimaryEvidence: (checked: boolean) => void;
  /** Label for local guidelines */
  regionLabel: string;
}

/**
 * Hook for managing source control state in chat input.
 */
export const useSourceControl = ({
  inputState = 'default',
}: UseSourceControlOptions = {}): UseSourceControlReturn => {
  const intl = useIntl();

  // Atom values
  const sourcesSelected = useAtomValue(sourcesSelectedAtom);
  const toggleState = useAtomValue(sourceControlToggleAtom);

  // Setters
  const setLocalGuidelines = useSetAtom(toggleLocalGuidelinesAtom);
  const setPrimaryEvidence = useSetAtom(togglePrimaryEvidenceAtom);

  const regionLabel = intl.formatMessage(useSourceControlMessages.regionLabel);

  const placeholder = useMemo(() => {
    return inputState === 'follow up'
      ? intl.formatMessage(useSourceControlMessages.v2FollowUp)
      : intl.formatMessage(useSourceControlMessages.v2Default);
  }, [inputState, intl]);

  const toggleLocalGuidelines = useCallback(
    (checked: boolean) => {
      setLocalGuidelines(checked);
    },
    [setLocalGuidelines]
  );

  const togglePrimaryEvidence = useCallback(
    (checked: boolean) => {
      setPrimaryEvidence(checked);
    },
    [setPrimaryEvidence]
  );

  return {
    sourcesSelected,
    toggleState,
    placeholder,
    toggleLocalGuidelines,
    togglePrimaryEvidence,
    regionLabel,
  };
};
